// Aba Vitrines do painel Aplicativo: quem está usando o link — ranking de
// visitas, chamados e pedidos, e quem tem app mas nunca teve o link aberto.
import { useEffect, useState } from 'react'
import { FiSearch, FiX, FiExternalLink } from 'react-icons/fi'
import { FotoPessoa } from '../foto/FotoPessoa'
import { API_APP, useApp, Carregando, Falha, Cabeca, Chip, Selo, Segmento, PAPEL, num, ha, quando, titulo } from './comum'

type V = {
  codigo: string; nome: string | null; papel: string | null; grupo: string | null; distrito: string | null; distrito_codigo: string | null
  tem_app: boolean; app_ativo: boolean; url: string
  aberturas: number; folhearam: number; chamados: number; pedidos: number; confirmados: number; artes: number; aberturas_sempre: number; ultima_visita: string | null
}
type Dados = { dias: number; contagem: Record<string, number>; resumo: Record<string, number>; vitrines: V[] }

const FILTROS: [string, string][] = [
  ['todas', 'Todas'], ['com_visita', 'Com visita no período'], ['com_pedido', 'Com pedido'], ['sem_visita', 'Com app, sem visita'], ['nunca_aberto', 'Link nunca aberto'],
]

export function Vitrines() {
  const [dias, setDias] = useState<'7' | '30'>('30')
  const [filtro, setFiltro] = useState('todas')
  const [busca, setBusca] = useState('')
  const [buscaViva, setBuscaViva] = useState('')
  useEffect(() => { const t = setTimeout(() => setBuscaViva(busca), 350); return () => clearTimeout(t) }, [busca])
  const q = new URLSearchParams({ dias, filtro })
  if (buscaViva.trim()) q.set('busca', buscaViva.trim())
  const { dados, erro, carregando, recarregar } = useApp<Dados>(`${API_APP}/vitrines?${q}`)

  if (!dados) return erro ? <Falha erro={erro} tentar={recarregar} /> : <Carregando />
  const r = dados.resumo
  const max = Math.max(1, ...dados.vitrines.map(v => v.aberturas))

  return (
    <div className="h-full min-h-0 flex flex-col gap-3 p-4 sm:p-5">
      <Cabeca
        titulo="Vitrines — quem está usando o link"
        sub={`Últimos ${dados.dias} dias · uma vitrine por pessoa (a de quem tem app, e toda que já teve visita)`}
        carregando={carregando} recarregar={recarregar}
        direita={<Segmento rotulo="Período" valor={dias} aoMudar={setDias} opcoes={[['7', '7 dias'], ['30', '30 dias']]} />}
        chips={<>
          <Chip tom="bom">{num(r.abertos)} abertas de {num(r.links)}</Chip>
          <Chip>{num(r.aberturas)} visitas · {num(r.folhearam)} folhearam</Chip>
          <Chip>{num(r.chamados)} chamados</Chip>
          <Chip>{num(r.pedidos)} pedidos</Chip>
          <Chip>{num(r.artes)} artes compartilhadas</Chip>
        </>}
      />
      <div className="flex items-center gap-1.5 flex-wrap shrink-0">
        {FILTROS.map(([k, t]) => <Chip key={k} ativo={filtro === k} onClick={() => setFiltro(k)} tom={k === 'nunca_aberto' || k === 'sem_visita' ? 'alerta' : 'neutro'}>{t} · {num(dados.contagem[k])}</Chip>)}
        <div className="neu-inset rounded-full flex items-center gap-2 px-3 py-1 w-full sm:w-56 sm:ml-auto">
          <FiSearch className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="nome, código, grupo…" aria-label="Buscar vitrine"
            className="bg-transparent outline-none text-[12.5px] flex-1 min-w-0" />
          {busca && <button onClick={() => setBusca('')} aria-label="Limpar busca"><FiX className="w-3.5 h-3.5 text-muted-foreground" /></button>}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hidden neu-card rounded-2xl">
        {dados.vitrines.length === 0 ? <p className="text-[12.5px] text-muted-foreground text-center py-10">Nada neste filtro.</p> : (
          <ul className="divide-y divide-border/60">
            {dados.vitrines.map((v, i) => (
              <li key={v.codigo} className="px-3.5 py-2.5 flex items-center gap-3">
                <span className="w-6 text-[11px] tabular-nums text-muted-foreground text-right shrink-0">{i + 1}</span>
                <FotoPessoa codigo={v.codigo} nome={titulo(v.nome)} tamanho={32} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[13px] font-semibold truncate max-w-full">{titulo(v.nome) || v.codigo}</span>
                    {!v.tem_app && <Selo title="Tem link, mas não tem acesso ao app">sem app</Selo>}
                    {v.tem_app && !v.app_ativo && <Selo>app desativado</Selo>}
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate"><span className="tabular-nums">{v.codigo}</span>{v.papel && <> · {PAPEL[v.papel]}</>}{v.grupo && <> · {titulo(v.grupo)}</>}</p>
                  <div className="h-1 rounded-full bg-foreground/[0.06] overflow-hidden mt-1 max-w-[18rem]">
                    <div className="h-full rounded-full bg-primary/70" style={{ width: `${(v.aberturas / max) * 100}%` }} />
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-0.5 text-right shrink-0">
                  <Num r="visitas" v={v.aberturas} forte />
                  <Num r="chamados" v={v.chamados} />
                  <Num r="pedidos" v={v.pedidos} />
                  <Num r="artes" v={v.artes} />
                </div>
                <span className="hidden md:block w-24 text-right text-[11px] text-muted-foreground shrink-0" title={v.ultima_visita ? quando(v.ultima_visita) : undefined}>
                  {v.ultima_visita ? ha(v.ultima_visita) : 'nunca aberto'}
                </span>
                <a href={v.url} target="_blank" rel="noopener noreferrer" aria-label={`Abrir a vitrine de ${titulo(v.nome)}`} className="text-muted-foreground hover:text-foreground shrink-0"><FiExternalLink className="w-4 h-4" /></a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function Num({ r, v, forte }: { r: string; v: number; forte?: boolean }) {
  return (
    <span className="flex flex-col leading-tight min-w-[3.2rem]">
      <span className={`text-[13px] tabular-nums ${forte ? 'font-semibold' : ''} ${v ? '' : 'text-muted-foreground/60'}`}>{num(v)}</span>
      <span className="text-[9.5px] text-muted-foreground">{r}</span>
    </span>
  )
}
